import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  connectAuthPost, getConnectToken, getConnectUser,
  clearConnectSession, normalizeUser
} from './connectApi';
import NotificationsBell from './NotificationsBell';
import './styles/ConnectDiscover.css';

export default function ConnectDiscover() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showMobileNav, setShowMobileNav] = useState(false);

  // Users loaded from API
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'local-guides', label: 'Local Guides' },
    { id: 'travelers', label: 'Travelers' },
    { id: 'following', label: 'Following' }
  ];


  useEffect(() => {
    if (!getConnectToken()) {
      navigate('/connect');
      return;
    }
    setUser(getConnectUser());

    const load = async () => {
      try {
        const result = await connectAuthPost('connect/users/discover');
        if (!result.success) {
          if (!getConnectToken()) {
            clearConnectSession();
            navigate('/connect');
          }
          return;
        }
        setUsers(result.data.users.map(normalizeUser));
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [navigate]);

  const handleFollow = async (userId) => {
    try {
      const result = await connectAuthPost('connect/users/follow', { user_id: userId });
      if (!result.success) return;

      const { is_following: isFollowing, followers } = result.data;

      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, isFollowing, followers } : u
      ));
    } catch (err) {
      console.error('Failed to toggle follow:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await connectAuthPost('connect/auth/logout');
    } catch (err) {
      // Best-effort — clear locally regardless
    }
    clearConnectSession();
    navigate('/connect');
  };

  const filteredUsers = users.filter(u => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      u.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.bio.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.favoriteDestinations.some(d => d.toLowerCase().includes(searchQuery.toLowerCase()));

    // Category filter
    let matchesFilter = true;
    if (activeFilter === 'local-guides') {
      matchesFilter = u.isLocalGuide;
    } else if (activeFilter === 'travelers') {
      matchesFilter = !u.isLocalGuide;
    } else if (activeFilter === 'following') {
      matchesFilter = u.isFollowing;
    }

    return matchesSearch && matchesFilter;
  });

  if (!user) return null;

  return (
    <div className="connect-discover-page">
      {/* Header */}
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <nav className="connect-profile-nav">
          <Link to="/connect/profile" className="connect-nav-link">Profile</Link>
          <Link to="/connect/discover" className="connect-nav-link active">Discover</Link>
          <Link to="/connect/messages" className="connect-nav-link">Messages</Link>
        </nav>
        <div className="connect-header-actions">
          <NotificationsBell />
          <button className="connect-logout-btn" onClick={handleLogout}>
            Log Out
          </button>
          <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <Link to="/connect/profile" className="connect-nav-link" onClick={() => setShowMobileNav(false)}>Profile</Link>
              <Link to="/connect/discover" className="connect-nav-link active" onClick={() => setShowMobileNav(false)}>Discover</Link>
              <Link to="/connect/messages" className="connect-nav-link" onClick={() => setShowMobileNav(false)}>Messages</Link>
            </div>
            <div className="connect-mobile-nav-footer">
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="connect-discover-main">
        {/* Search & Filters */}
        <div className="connect-discover-header">
          <h1>Discover People</h1>
          <p>Connect with LGBTQ+ travelers and local guides across the Caribbean</p>

          <div className="connect-search-bar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name, location, or destination..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="connect-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            )}
          </div>

          <div className="connect-filters">
            {filters.map(filter => (
              <button
                key={filter.id}
                className={`connect-filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
                {filter.id === 'following' && (
                  <span className="connect-filter-count">
                    {users.filter(u => u.isFollowing).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Users Grid */}
        <div className="connect-users-grid">
          {isLoading ? (
            <div className="connect-no-results">
              <h3>Loading...</h3>
              <p>Finding travelers and local guides</p>
            </div>
          ) : filteredUsers.length > 0 ? (
            filteredUsers.map(person => (
              <div key={person.id} className="connect-user-card">
                <div className="connect-user-card-header">
                  {person.coverPhoto && (
                    <img src={person.coverPhoto} alt="" className="connect-card-cover" />
                  )}
                  <div className="connect-user-avatar">
                    {person.profilePicture ? (
                      <img src={person.profilePicture} alt={person.fullName} />
                    ) : (
                      <div className="connect-user-avatar-placeholder">
                        {person.fullName.charAt(0)}
                      </div>
                    )}
                  </div>
                  {person.isLocalGuide && (
                    <span className="connect-local-guide-badge">Local Guide</span>
                  )}
                </div>

                <div className="connect-user-card-body">
                  <h3 className="connect-user-name">{person.fullName}</h3>
                  <span className="connect-user-pronouns">{person.pronouns}</span>
                  <p className="connect-user-location">📍 {person.location}</p>

                  <div className="connect-user-tags">
                    {person.genderIdentity && (
                      <span className="connect-user-tag">{person.genderIdentity}</span>
                    )}
                    {person.sexualOrientation && (
                      <span className="connect-user-tag">{person.sexualOrientation}</span>
                    )}
                  </div>

                  <p className="connect-user-bio">{person.bio}</p>

                  <div className="connect-user-destinations">
                    {person.favoriteDestinations.slice(0, 3).map(dest => (
                      <span key={dest} className="connect-dest-tag">{dest}</span>
                    ))}
                  </div>

                  <div className="connect-user-stats">
                    <span><strong>{person.followers}</strong> followers</span>
                    <span><strong>{person.following}</strong> following</span>
                  </div>
                </div>

                <div className="connect-user-card-actions">
                  <button
                    className={`connect-follow-btn ${person.isFollowing ? 'following' : ''}`}
                    onClick={() => handleFollow(person.id)}
                  >
                    {person.isFollowing ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Following
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="8.5" cy="7" r="4"/>
                          <line x1="20" y1="8" x2="20" y2="14"/>
                          <line x1="23" y1="11" x2="17" y2="11"/>
                        </svg>
                        Follow
                      </>
                    )}
                  </button>
                  <button 
                    className="connect-view-btn"
                    onClick={() => navigate('/connect/user/' + person.id)}
                  >
                    View Profile
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="connect-no-results">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <h3>No results found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </main>

    </div>
  );
}
